<template>
  <div class="layout" :class="{ 'sidebar-collapsed': isSidebarCollapsed }">
    <aside v-if="!isSidebarCollapsed" class="layout__sidebar">
      <PlanNavigation @toggle="toggleSidebar" />
    </aside>
    <main class="layout__main">
      <HeaderBar 
        :title="headerTitleWithDate" 
        :dateText="headerDate" 
        :live="headerLive"
        :is-sidebar-collapsed="isSidebarCollapsed"
        @toggle-sidebar="toggleSidebar"
        :userName="currentUserName"
        :userRole="currentUserRole"
        :userRank="currentUserRank"
      />
      <BookSectionNavigation v-if="$route.path.startsWith('/kps')" />
      <div class="layout__content">
        
        <router-view v-slot="{ Component }">
          <keep-alive include="PlanView">
            <component :is="Component" />
          </keep-alive>
        </router-view>
      </div>
    </main>
  </div>
</template>

<script>
import PlanNavigation from "@/components/navigation/PlanNavigation.vue";
import HeaderBar from "@/components/layout/HeaderBar.vue";
import BookSectionNavigation from "@/components/navigation/BookSectionNavigation.vue";
import { getCurrentUser, getUserRoles } from '@/api/user.js';

export default {
  components: {
    PlanNavigation,
    HeaderBar,
    BookSectionNavigation,
  },
  data() {
    return {
      isSidebarCollapsed: false,
      currentUserName: '',
      currentUserRole: '',
      currentUserRank: '',
    };
  },
  provide() {
    return {
      setSidebarCollapsed: (collapsed) => {
        this.isSidebarCollapsed = !!collapsed;
      },
    };
  },
  computed: {
    headerTitle() {
      // По ТЗ 03.09.2026: приоритеты выбора заголовка шапки:
      //   1. meta.headerTitleI18n (i18n-ключ, самый явный)
      //   2. авто-детект по префиксу пути → $t('chpo.title') / $t('kps.title')
      //      (покрывает большинство роутов без правки router.js)
      //   3. meta.headerTitle (русский fallback)
      const key = this.$route.meta?.headerTitleI18n
      if (key) return this.$t(key)
      const path = this.$route?.path || ''
      const meta = this.$route.meta || {}
      // Стабильные i18n-ключи для типовых префиксов путей.
      if (path === '/' || path === '') return meta.headerTitle || this.$t('sidebar.main')
      if (path === '/admin-landing') return this.$t('routes.adminPanel')
      if (path.startsWith('/admin/users')) return this.$t('routes.adminUsers')
      if (path.startsWith('/admin/dictionaries')) return this.$t('routes.adminDict')
      if (path === '/settings/ranks') return this.$t('routes.dictRanks')
      if (path === '/settings/positions') return this.$t('routes.dictPositions')
      if (path === '/settings/stpn-types') return this.$t('routes.dictStpn')
      if (path === '/settings/user-categories') return this.$t('routes.dictUserCat')
      if (path === '/settings/vehicle-types') return this.$t('routes.dictVehicleTypes')
      if (path === '/settings/meal-types') return this.$t('routes.dictMealTypes')
      if (path === '/settings/check-types') return this.$t('routes.dictCheckTypes')
      if (path === '/map-scheme') return this.$t('map.title')
      if (path === '/tsog') return 'ТСОГ'
      if (path.startsWith('/chpo/daily-schedule')) return this.$t('routes.chpoDaily')
      if (path.startsWith('/chpo/workload')) return this.$t('routes.chpoWorkload')
      if (path.startsWith('/chpo/sector-select')) return this.$t('routes.chpoSectorSelect')
      if (path.startsWith('/chpo/')) return this.$t('chpo.title')
      if (path.startsWith('/kps/')) return this.$t('kps.title')
      return meta.headerTitle || this.$t('chpo.title')
    },
    headerTitleWithDate() {
      const base = this.headerTitle
      const dateISO = this.$route.meta?.dateISO
      return dateISO ? `${base} (${dateISO})` : base
    },
    headerDate() {
      return this.$route.meta?.headerDate || '22/23.04.2025  22:48 четверг'
    },
    headerLive() {
      return this.$route.meta?.headerLive ?? true
    },
  },
  async mounted() {
    const user = await getCurrentUser();
    if (user && user.full_name) {
      this.currentUserName = user.full_name;
    }
    if (user && user.rank) {
      this.currentUserRank = user.rank;
    }

    try {
      const roleRaw = await getUserRoles();
      const toRuRole = (code) => {
        const v = String(code || '').trim().toUpperCase();
        const map = {
          NZ: 'НЗ',
          ZNZ: 'ЗНЗ',
          ZNZ_VIR: 'ЗНЗ по ВИР',
          STT: 'СТТ',
          STZ: 'СТЗ',
          DJ: 'ДЖ',
          DSS: 'ДСС',
          DOTS: 'ДОТС',
          WAREHOUSE: 'СКЛАД',
          RESPONSIBLE: 'ОТВЕТСТВЕННЫЙ',
        };
        return map[v] || code || '';
      };

      if (Array.isArray(roleRaw)) {
        const first = roleRaw.filter(Boolean)[0];
        this.currentUserRole = toRuRole(first);
      } else if (typeof roleRaw === 'string') {
        // If backend returned comma-separated list, keep only first.
        const first = roleRaw.split(',')[0];
        this.currentUserRole = toRuRole(first);
      } else if (roleRaw && typeof roleRaw === 'object') {
        this.currentUserRole =
          toRuRole(roleRaw.abbr) ||
          roleRaw.code ||
          roleRaw.name ||
          roleRaw.position_name ||
          '';
      } else {
        this.currentUserRole = '';
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to load user role:', e);
      this.currentUserRole = '';
    }
  },
  methods: {
    toggleSidebar() {
      this.isSidebarCollapsed = !this.isSidebarCollapsed;
    },
  },
};
</script>

<style scoped>
.layout {
  --sidebar-width: 18vw;
}

.layout__sidebar {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: var(--sidebar-width);
  z-index: 1000;
  overflow-y: auto;
  transition: transform 0.3s ease;
}

.layout__main {
  margin-left: var(--sidebar-width);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  min-width: 0;
  transition: margin-left 0.3s ease;
}

/* Когда сайдбар скрыт */
.layout.sidebar-collapsed .layout__main {
  margin-left: 0;
}

.layout__content {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  box-sizing: border-box;
  min-width: 0;
  overflow-x: hidden;
}

.layout__content :deep(> *) {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
</style>
